import { useUser } from "@modules/auth/hooks/useUser"
import { useCurrentBox } from "@modules/box/useBox"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { getCurrencySymbol } from "@modules/currency/currency"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { BoxOfferListSelection } from "@modules/promoCode/components/boxOfferListSelection"
import { ProgramBundleListSelection } from "@modules/promoCode/components/programBundleListSelection"
import { ProgramListSelection } from "@modules/promoCode/components/programListSelection"
import { usePromoCodeForm } from "@modules/promoCode/hooks/usePromoCodeForm"
import { BoxPromoCode } from "@modules/promoCode/types/boxPromoCodeTypes"
import { PromoCode } from "@modules/promoCode/types/promoCodeTypes"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HUStack } from "@modules/ui/components/layout"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import styled, { useTheme } from "styled-components"

export const PromoCodeFormView: React.FC<{ updatedPromoCode?: PromoCode | BoxPromoCode }> = ({ updatedPromoCode }) => {
  const theme = useTheme()
  const user = useUser()
  const language = useLanguage()
  const { result: box } = useCurrentBox()

  const {
    formik: promoCodeFormik,
    isSubmitting,
    promoCodeTypesOptions,
    expirationOptions,
    applicationDurationOptions,
    maxAttendeesOptions,
    onSelectProgram,
    onSelectAllPrograms,
    onSelectProgramBundle,
    onSelectAllProgramBundles,
    onSelectBoxOffer,
    onSelectAllBoxOffers,
  } = usePromoCodeForm(updatedPromoCode)

  return (
    <Container gap={0}>
      <HUText fontStyle="TL">{word("promoCode.form.title")}</HUText>
      <HUText fontStyle="BM" className="mb-6 mt-4">
        {word("promoCode.form.description")}
      </HUText>
      <InputStack gap={0}>
        <FormikInput
          flex
          formikStyle={{ display: "flex", flexGrow: 1 }}
          label={word("promoCode.form.input.title.label")}
          name="title"
          isRequiredInput
          getFieldProps={promoCodeFormik.getFieldProps}
          error={getFormErrorMessage("title", promoCodeFormik)}
          isInvalid={isFormFieldInValid("title", promoCodeFormik)}
          setFieldValue={promoCodeFormik.setFieldValue}
          placeHolder={word("promoCode.form.input.title.placeholder")}
          textProps={{ maxLength: 40 }}
        />
      </InputStack>
      <InputStack gap={0}>
        <FormikInput
          flex
          formikStyle={{ display: "flex", flexGrow: 1 }}
          label={word("promoCode.form.input.code.label")}
          name="code"
          isRequiredInput
          getFieldProps={promoCodeFormik.getFieldProps}
          isInvalid={isFormFieldInValid("code", promoCodeFormik)}
          setFieldValue={promoCodeFormik.setFieldValue}
          error={getFormErrorMessage("code", promoCodeFormik)}
          placeHolder={word("promoCode.form.input.code.placeholder")}
          textProps={{
            maxLength: 30,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              promoCodeFormik.setFieldValue("code", e.target.value.toUpperCase())
            },
          }}
        />
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          className="mb-4"
          style={{ whiteSpace: "pre-line", marginTop: -8 }}
        >
          {word("promoCode.form.input.code.description")}
        </HUText>
      </InputStack>
      <InputStack gap={0}>
        <FormikInput
          formikStyle={{ flexGrow: 1 }}
          label={word("promoCode.form.input.type.label")}
          isRequiredInput
          getFieldProps={promoCodeFormik.getFieldProps}
          name="type"
          setFieldValue={promoCodeFormik.setFieldValue}
          isInvalid={isFormFieldInValid("type", promoCodeFormik)}
          error={getFormErrorMessage("type", promoCodeFormik)}
          type="dropdown"
          dropdownProps={{
            value: promoCodeFormik.values.type,
            options: promoCodeTypesOptions,
          }}
        />
        <FormikInput
          isRequiredInput
          formikStyle={{ display: "flex", flexGrow: 1 }}
          isInvalid={isFormFieldInValid("value", promoCodeFormik)}
          getFieldProps={() => ({
            ...promoCodeFormik.getFieldProps("value"),
            "data-testid": "value",
          })}
          name="value"
          error={getFormErrorMessage("value", promoCodeFormik)}
          type="number"
          numberProps={{
            currency: box?.stripeAccountCurrency ?? user?.stripeAccountCurrency,
            mode: promoCodeFormik.values.type === CouponType.PERCENT ? "decimal" : "currency",
            min: 0,
            max: promoCodeFormik.values.type === CouponType.PERCENT ? 100 : undefined,
          }}
          setFieldValue={promoCodeFormik.setFieldValue}
          rightButton={
            <HUText fontStyle="TM" className="ml-1">
              {promoCodeFormik.values.type === CouponType.DIRECT
                ? getCurrencySymbol(promoCodeFormik.values.currency, language)
                : " %"}
            </HUText>
          }
        />
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          className="mb-4"
          style={{ whiteSpace: "pre-line", marginTop: -8 }}
        >
          {word("promoCode.form.input.type.description")}
        </HUText>
      </InputStack>
      <InputStack gap={0}>
        <FormikInput
          formikStyle={{ flexGrow: 1 }}
          label={word("promoCode.form.input.expiration.label")}
          isRequiredInput
          getFieldProps={promoCodeFormik.getFieldProps}
          name="noExpirationDate"
          setFieldValue={promoCodeFormik.setFieldValue}
          isInvalid={isFormFieldInValid("noExpirationDate", promoCodeFormik)}
          error={getFormErrorMessage("noExpirationDate", promoCodeFormik)}
          type="dropdown"
          dropdownProps={{
            value: promoCodeFormik.values.noExpirationDate,
            options: expirationOptions,
            onChange: (e) => {
              if (e.target.value) {
                promoCodeFormik.setFieldValue("expirationDate", undefined)
                promoCodeFormik.setFieldTouched("expirationDate", false)
              }
            },
          }}
        />

        {!promoCodeFormik.values.noExpirationDate && (
          <FormikInput
            formikStyle={{ flexGrow: 1 }}
            isRequiredInput
            getFieldProps={promoCodeFormik.getFieldProps}
            name="expirationDate"
            type="calendar"
            isInvalid={isFormFieldInValid("expirationDate", promoCodeFormik)}
            error={getFormErrorMessage("expirationDate", promoCodeFormik)}
            calendarProps={{
              minDate: dayjs().toDate(),
              showIcon: true,
              dateFormat: displayedFullDateFormat,
              inputStyle: { textTransform: "capitalize" },
              maxDate: dayjs().add(1, "year").toDate(),
            }}
          />
        )}
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          className="mb-4"
          style={{ whiteSpace: "pre-line", marginTop: -8 }}
        >
          {word("promoCode.form.input.expirationDate.description")}
        </HUText>
      </InputStack>
      <InputStack gap={0}>
        <FormikInput
          formikStyle={{ flexGrow: 1 }}
          label={word("promoCode.form.input.applicationDuration.label")}
          isRequiredInput
          getFieldProps={promoCodeFormik.getFieldProps}
          name="noDurationLimit"
          setFieldValue={promoCodeFormik.setFieldValue}
          isInvalid={isFormFieldInValid("noDurationLimit", promoCodeFormik)}
          error={getFormErrorMessage("noDurationLimit", promoCodeFormik)}
          type="dropdown"
          dropdownProps={{
            value: promoCodeFormik.values.noDurationLimit,
            options: applicationDurationOptions,
            onChange: (e) => {
              if (e.target.value) promoCodeFormik.setFieldValue("durationInMonths", undefined)
              else promoCodeFormik.setFieldValue("durationInMonths", 1)
            },
          }}
        />

        {!promoCodeFormik.values.noDurationLimit && (
          <FormikInput
            isRequiredInput
            formikStyle={{ display: "flex", flexGrow: 1 }}
            isInvalid={isFormFieldInValid("durationInMonths", promoCodeFormik)}
            getFieldProps={promoCodeFormik.getFieldProps}
            name="durationInMonths"
            error={getFormErrorMessage("durationInMonths", promoCodeFormik)}
            type="number"
            numberProps={{
              min: 0,
              max: 120,
              maxFractionDigits: 0,
            }}
            setFieldValue={promoCodeFormik.setFieldValue}
            rightButton={
              <HUText fontStyle="TM" className="ml-1">
                {word("global.month.label", { count: promoCodeFormik.values.durationInMonths ?? 0 })}
              </HUText>
            }
          />
        )}
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          className="mb-4"
          style={{ whiteSpace: "pre-line", marginTop: -8 }}
        >
          {word("promoCode.form.input.applicationDuration.description")}
        </HUText>
      </InputStack>
      <InputStack gap={0}>
        <FormikInput
          formikStyle={{ flexGrow: 1 }}
          label={word("promoCode.form.input.maxAttendees.label")}
          isRequiredInput
          getFieldProps={promoCodeFormik.getFieldProps}
          name="maxAttendeesLimited"
          setFieldValue={promoCodeFormik.setFieldValue}
          isInvalid={isFormFieldInValid("maxAttendeesLimited", promoCodeFormik)}
          error={getFormErrorMessage("maxAttendeesLimited", promoCodeFormik)}
          type="dropdown"
          dropdownProps={{
            value: promoCodeFormik.values.maxAttendeesLimited,
            options: maxAttendeesOptions,
            onChange: (e) => {
              if (e.target.value) promoCodeFormik.setFieldValue("maxAttendees", 1)
              else promoCodeFormik.setFieldValue("maxAttendees", undefined)
            },
          }}
        />
        {promoCodeFormik.values.maxAttendeesLimited && (
          <FormikInput
            isRequiredInput
            isInvalid={isFormFieldInValid("maxAttendees", promoCodeFormik)}
            getFieldProps={promoCodeFormik.getFieldProps}
            formikStyle={{ display: "flex", flexGrow: 1 }}
            name="maxAttendees"
            error={getFormErrorMessage("maxAttendees", promoCodeFormik)}
            type="number"
            numberProps={{
              min: 0,
              max: 1e5,
              maxFractionDigits: 0,
            }}
            setFieldValue={promoCodeFormik.setFieldValue}
            rightButton={
              <HUText fontStyle="TM" className="ml-1">
                {word("global.customer.label", { count: promoCodeFormik.values.maxAttendees ?? 0 })}
              </HUText>
            }
          />
        )}
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          className="mb-4"
          style={{ whiteSpace: "pre-line", marginTop: -8 }}
        >
          {word("promoCode.form.input.maxAttendees.description")}
        </HUText>
      </InputStack>
      <InputStack gap={0}>
        <FormikInput
          formikStyle={{ marginBottom: -5 }}
          label={word("promoCode.form.input.filter.label")}
          type="switchInline"
          textSwitch={word("promoCode.form.input.onlyNewCustomer.label")}
          name="onlyNewCustomer"
          getFieldProps={promoCodeFormik.getFieldProps}
          isInvalid={isFormFieldInValid("onlyNewCustomer", promoCodeFormik)}
          error={getFormErrorMessage("onlyNewCustomer", promoCodeFormik)}
          setFieldValue={promoCodeFormik.setFieldValue}
        />
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          className="mb-4"
          style={{ whiteSpace: "pre-line", marginTop: -8 }}
        >
          {word("promoCode.form.input.onlyNewCustomer.description")}
        </HUText>
      </InputStack>
      <HUText type="label" color={theme.neutralColor700} fontStyle="LS" style={{ lineHeight: "21px", marginBottom: 7 }}>
        {(!!box
          ? word("promoCode.form.input.relatedOffers")
          : word("promoCode.form.input.relatedPrograms")
        ).toUpperCase()}
        <HUText fontStyle="LL" color={theme.necessaryInput}>
          {" *"}
        </HUText>
      </HUText>

      {!!box ? (
        <BoxOfferListSelection
          onSelectBoxOffer={onSelectBoxOffer}
          onSelectAllBoxOffers={onSelectAllBoxOffers}
          selectedBoxOfferIds={promoCodeFormik.values.boxOffers || []}
          promoCodeType={promoCodeFormik.values.type}
          promoCodeValue={promoCodeFormik.values.value}
          promoCodeDuration={promoCodeFormik.values.durationInMonths}
          error={getFormErrorMessage("boxOffers", promoCodeFormik)}
        />
      ) : (
        <>
          <ProgramListSelection
            onSelectProgram={onSelectProgram}
            onSelectAllPrograms={onSelectAllPrograms}
            selectedProgramIds={promoCodeFormik.values.programs || []}
            promoCodeType={promoCodeFormik.values.type}
            promoCodeValue={promoCodeFormik.values.value}
            promoCodeDuration={promoCodeFormik.values.durationInMonths}
            error={getFormErrorMessage("programs", promoCodeFormik)}
          />

          <ProgramBundleListSelection
            onSelectProgram={onSelectProgramBundle}
            onSelectAllPrograms={onSelectAllProgramBundles}
            selectedProgramIds={promoCodeFormik.values.programBundles || []}
            promoCodeType={promoCodeFormik.values.type}
            promoCodeValue={promoCodeFormik.values.value}
            promoCodeDuration={promoCodeFormik.values.durationInMonths}
            error={getFormErrorMessage("programBundles", promoCodeFormik)}
          />
        </>
      )}
      <HUButton
        type="Default"
        colorType="Primary"
        size="M"
        className="w-full mt-5 mb-5"
        loading={isSubmitting}
        onClick={promoCodeFormik.submitForm}
        text={!!updatedPromoCode ? word("promoCode.form.submit.update") : word("promoCode.form.submit.create")}
        data-testid="promocode-form-validate"
      />
    </Container>
  )
}

const Container = styled(HUStack)`
  > .p-disabled,
  .p-component:disabled {
    background-color: ${({ theme }) => theme.neutralColor100};
    border-color: ${({ theme }) => theme.neutralColor100};
  }
`

const InputStack = styled(HUStack)`
  margin-bottom: 10px;
`
